import React, { useState } from 'react';
import { HelpCircle, MapPin } from 'lucide-react';
import { useT } from '../../services/i18n';

const PARTS = [
  { key: 'width', text: '205', color: 'bg-orange-500', ring: 'ring-orange-300' },
  { key: 'height', text: '55', color: 'bg-sky-600', ring: 'ring-sky-300' },
  { key: 'radial', text: 'R', color: 'bg-slate-700', ring: 'ring-slate-300' },
  { key: 'diameter', text: '16', color: 'bg-emerald-600', ring: 'ring-emerald-300' },
  { key: 'load', text: '91', color: 'bg-violet-600', ring: 'ring-violet-300' },
  { key: 'speed', text: 'V', color: 'bg-rose-600', ring: 'ring-rose-300' },
];

const TyreSidewallGuide = () => {
  const { t } = useT();
  const [active, setActive] = useState('width');
  const current = PARTS.find((p) => p.key === active);
  return (
    <section className="bg-white rounded-lg shadow-sm p-6 md:p-8 mt-8" id="guide-pneu" data-testid="tyre-sidewall-guide">
      <div className="flex items-start gap-3 mb-5">
        <HelpCircle size={22} className="text-orange-500 mt-0.5 shrink-0" />
        <div>
          <h2 className="text-base md:text-lg font-bold text-gray-900">{t('tw.sg.title')}</h2>
          <p className="text-sm text-gray-600 mt-1">{t('tw.sg.sub')} <strong>205/55 R16 91V</strong>.</p>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-center">
        <div className="lg:col-span-3">
          <div className="relative mx-auto max-w-2xl rounded-[2.5rem] bg-slate-900 px-4 py-8 sm:px-8 shadow-inner" aria-hidden="true">
            <div className="absolute inset-3 rounded-[2rem] border border-slate-700/70 pointer-events-none" />
            <div className="absolute inset-6 rounded-[1.6rem] border border-dashed border-slate-600/50 pointer-events-none" />
            <p className="relative text-center text-[10px] tracking-[0.35em] text-slate-400 mb-3">OTOPIECES · TUBELESS · RADIAL · M+S</p>
            <div className="relative flex flex-nowrap items-end justify-center gap-0.5 sm:gap-1.5 font-mono font-black text-2xl sm:text-4xl lg:text-5xl text-white select-none" data-testid="tyre-sidewall-marking">
              {PARTS.map((p, i) => (
                <React.Fragment key={p.key}>
                  {i === 1 && <span className="text-slate-400">/</span>}
                  {i === 4 && <span className="w-3 sm:w-5" />}
                  <button type="button" onMouseEnter={() => setActive(p.key)} onFocus={() => setActive(p.key)} onClick={() => setActive(p.key)} data-testid={`sidewall-part-${p.key}`} aria-pressed={active === p.key}
                    className={`rounded-lg px-1.5 sm:px-2.5 py-1 transition-all ${active === p.key ? `${p.color} ring-4 ${p.ring} scale-105` : 'hover:bg-slate-700/60'}`}>
                    {p.text}
                  </button>
                </React.Fragment>
              ))}
            </div>
            <div className="relative mt-4 flex flex-wrap justify-center gap-x-4 gap-y-1 text-[11px] text-slate-300">
              {PARTS.map((p) => <span key={p.key} className={`inline-flex items-center gap-1 ${active === p.key ? 'text-white font-semibold' : ''}`}><span className={`w-2 h-2 rounded-full ${p.color}`} /> {t(`tw.sg.${p.key}`)}</span>)}
            </div>
          </div>
        </div>
        <div className="lg:col-span-2 space-y-3">
          <div className={`rounded-xl border-l-4 p-4 bg-gray-50 ${current.color.replace('bg-', 'border-')}`} data-testid="sidewall-explanation">
            <p className="text-xs uppercase tracking-wide text-gray-500">{t(`tw.sg.${current.key}`)}</p>
            <p className="text-2xl font-black text-gray-900 font-mono">{current.text}</p>
            <p className="text-sm text-gray-700 mt-1 leading-relaxed">{t(`tw.sg.${current.key}Desc`)}</p>
          </div>
          <div className="rounded-xl border border-gray-200 p-4 text-sm text-gray-700 flex gap-2">
            <MapPin size={16} className="text-orange-500 shrink-0 mt-0.5" />
            <p><strong>{t('tw.sg.where')}</strong> {t('tw.sg.whereText')} <strong>{t('tw.byVehicle')}</strong> {t('tw.sg.whereEnd')}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TyreSidewallGuide;
